import type { PreparedConnection } from "@t3tools/client-runtime/connection";
import {
  VM_DESKTOP_STREAM_PATH,
  VM_DESKTOP_STREAM_PROTOCOL,
  type VmDesktopOpenResult,
} from "@t3tools/contracts";

export { VM_DESKTOP_STREAM_PROTOCOL };

/**
 * Resolves the private RFB path against the environment connection that
 * answered the open RPC. The application WebSocket ticket is intentionally
 * discarded; the desktop stream receives its own ticket as a subprotocol.
 */
export function resolveVmDesktopStream(input: {
  readonly connection: Pick<PreparedConnection, "httpBaseUrl" | "socketUrl">;
  readonly opened: VmDesktopOpenResult;
  /** Bind the admitted path to the request that produced it when available. */
  readonly environmentId?: string;
  readonly threadId?: string;
}): { readonly url: string; readonly protocols: readonly [string, string] } {
  let base: URL;
  try {
    base = new URL(input.connection.socketUrl);
  } catch (error) {
    throw new Error("The active VM connection has an invalid websocket endpoint.", {
      cause: error,
    });
  }

  if (base.username || base.password) {
    throw new Error("The active VM connection endpoint must not contain userinfo.");
  }
  const streamProtocol = (() => {
    switch (base.protocol) {
      case "wss:":
        return "wss:";
      case "ws:":
        return "ws:";
      default:
        throw new Error("The active VM connection has an unsupported endpoint protocol.");
    }
  })();
  if (!base.host) throw new Error("The active VM connection endpoint has no host.");

  base.protocol = streamProtocol;
  base.pathname = "/";
  base.search = "";
  base.hash = "";

  // The server returns a path, never a new origin. Reject absolute and
  // network-path references before URL resolution so the active endpoint is
  // always the source of the websocket protocol and origin.
  if (!input.opened.path.startsWith("/") || input.opened.path.startsWith("//")) {
    throw new Error("VM desktop stream admission must return a relative path.");
  }

  let endpoint: URL;
  try {
    endpoint = new URL(input.opened.path, base);
  } catch (error) {
    throw new Error("VM desktop stream admission returned an invalid path.", { cause: error });
  }
  endpoint.protocol = streamProtocol;
  endpoint.hash = "";
  if (endpoint.host !== base.host) {
    throw new Error("VM desktop stream path must stay on the active environment endpoint.");
  }
  if (endpoint.username || endpoint.password) {
    throw new Error("VM desktop stream path must not contain userinfo.");
  }
  if (endpoint.pathname !== VM_DESKTOP_STREAM_PATH) {
    throw new Error("VM desktop stream admission returned an unexpected path.");
  }
  const allowedQuery = new Set(["environmentId", "threadId"]);
  for (const key of endpoint.searchParams.keys()) {
    if (!allowedQuery.has(key)) {
      throw new Error(
        `VM desktop stream admission returned an unexpected query parameter: ${key}.`,
      );
    }
  }
  for (const [name, expected] of [
    ["environmentId", input.environmentId],
    ["threadId", input.threadId],
  ] as const) {
    if (expected === undefined) continue;
    const values = endpoint.searchParams.getAll(name);
    if (values.length !== 1 || values[0] !== expected) {
      throw new Error(`VM desktop stream admission returned an unexpected ${name}.`);
    }
  }

  return {
    url: endpoint.toString(),
    protocols: [VM_DESKTOP_STREAM_PROTOCOL, input.opened.ticket],
  };
}
